import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { isFirebaseConfigured } from '../firebase/config';
import { enableFirestoreOffline } from '../firebase/app';
import {
  addToWishlist,
  removeFromWishlist,
  subscribeToUserWishlist,
  mergeAnonymousWishlist,
  getUserWishlist,
  ensureFirebaseAuthForUser,
  signOutFirebase,
  getCachedWishlistIds,
  setCachedWishlistIds,
  clearWishlistCache,
  getAnonymousWishlistItems,
  getAnonymousWishlistIds,
  addAnonymousWishlistItem,
  removeAnonymousWishlistItem,
  clearAnonymousWishlist,
} from '../services/wishlist';
import { debounce } from '../util/debounce';
import { useRouteConfiguration } from './routeConfigurationContext';
import { pathByRouteName } from '../util/routes';

const WishlistContext = createContext(null);

const idsToSet = ids => new Set(ids || []);

/**
 * Global wishlist state with Firestore realtime sync, local cache, and anonymous mode.
 */
export const WishlistProvider = props => {
  const { children } = props;
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const currentUser = useSelector(state => state.user.currentUser);
  const userId = currentUser?.id?.uuid || null;

  const [items, setItems] = useState([]);
  const [listingIds, setListingIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [syncingIds, setSyncingIds] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);

  const pendingWritesRef = useRef(new Set());
  const optimisticRef = useRef(new Map());
  const unsubscribeRef = useRef(null);

  const firebaseEnabled = isFirebaseConfigured();

  // Hydrate from cache or anonymous storage on mount / user change
  useEffect(() => {
    if (!firebaseEnabled) {
      return;
    }

    if (userId) {
      const cached = getCachedWishlistIds(userId);
      if (cached.length) {
        setListingIds(idsToSet(cached));
      }
    } else if (!isAuthenticated) {
      setListingIds(idsToSet(getAnonymousWishlistIds()));
    }
  }, [userId, isAuthenticated, firebaseEnabled]);

  // Firebase auth + realtime listener when Sharetribe user is logged in
  useEffect(() => {
    if (!firebaseEnabled || typeof window === 'undefined') {
      return undefined;
    }

    enableFirestoreOffline();

    if (!isAuthenticated || !userId) {
      setFirebaseReady(false);
      setItems([]);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      signOutFirebase().catch(() => {});
      if (!isAuthenticated) {
        clearWishlistCache();
        setListingIds(idsToSet(getAnonymousWishlistIds()));
      }
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const setup = async () => {
      try {
        await ensureFirebaseAuthForUser(userId);

        const anonymousItems = getAnonymousWishlistItems();
        if (anonymousItems.length) {
          await mergeAnonymousWishlist(userId, anonymousItems);
          clearAnonymousWishlist();
        }

        if (cancelled) {
          return;
        }

        unsubscribeRef.current = subscribeToUserWishlist(
          userId,
          ({ items: nextItems, listingIds: nextIds }) => {
            setItems(nextItems);
            setListingIds(idsToSet(nextIds));
            setCachedWishlistIds(userId, nextIds);
            setLoading(false);
            setFirebaseReady(true);
            setHasMore(nextItems.length >= 24);
          },
          err => {
            setError(err);
            setLoading(false);
          }
        );
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setLoading(false);
        }
      }
    };

    setup();

    return () => {
      cancelled = true;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [firebaseEnabled, isAuthenticated, userId]);

  const applyOptimistic = useCallback((listingId, wishlisted) => {
    setListingIds(prev => {
      const next = new Set(prev);
      if (wishlisted) {
        next.add(listingId);
      } else {
        next.delete(listingId);
      }
      return next;
    });
    optimisticRef.current.set(listingId, wishlisted);
  }, []);

  const flushWrite = useCallback(
    async (listingId, listing, shouldBeWishlisted) => {
      if (pendingWritesRef.current.has(listingId)) {
        return;
      }
      pendingWritesRef.current.add(listingId);
      setSyncingIds(prev => new Set(prev).add(listingId));

      try {
        if (!userId) {
          if (shouldBeWishlisted) {
            addAnonymousWishlistItem(listing);
          } else {
            removeAnonymousWishlistItem(listingId);
          }
          setListingIds(idsToSet(getAnonymousWishlistIds()));
          return;
        }

        // Firestore rules require Firebase Auth; wait for custom-token sign-in.
        await ensureFirebaseAuthForUser(userId);

        if (shouldBeWishlisted) {
          await addToWishlist(userId, listing);
        } else {
          await removeFromWishlist(userId, listingId);
        }
      } catch (err) {
        const previous = optimisticRef.current.get(listingId);
        if (previous !== undefined) {
          applyOptimistic(listingId, !shouldBeWishlisted);
        }
        setError(err);
        throw err;
      } finally {
        pendingWritesRef.current.delete(listingId);
        optimisticRef.current.delete(listingId);
        setSyncingIds(prev => {
          const next = new Set(prev);
          next.delete(listingId);
          return next;
        });
      }
    },
    [userId, applyOptimistic]
  );

  const debouncersRef = useRef(new Map());
  const scheduleWrite = useCallback(
    (listingId, listing, wishlisted) => {
      if (!debouncersRef.current.has(listingId)) {
        debouncersRef.current.set(
          listingId,
          debounce((l, w) => flushWrite(listingId, l, w), 350)
        );
      }
      debouncersRef.current.get(listingId)(listing, wishlisted);
    },
    [flushWrite]
  );

  const redirectToLogin = useCallback(() => {
    const loginPath = pathByRouteName('LoginPage', routeConfiguration);
    history.push({
      pathname: loginPath,
      state: { from: `${window.location.pathname}${window.location.search}` },
    });
  }, [history, routeConfiguration]);

  /**
   * Toggle wishlist for a listing. Guests use anonymous localStorage.
   * Logged-in users require Firebase; unauthenticated toggle with requireAuth redirects to login.
   */
  const toggleWishlist = useCallback(
    (listing, options = {}) => {
      const { requireAuth = true } = options;
      const listingId = listing?.id?.uuid;
      if (!listingId || !firebaseEnabled) {
        return Promise.resolve(false);
      }

      if (requireAuth && !isAuthenticated) {
        redirectToLogin();
        return Promise.resolve(false);
      }

      const currentlyWishlisted = listingIds.has(listingId);
      const nextWishlisted = !currentlyWishlisted;

      applyOptimistic(listingId, nextWishlisted);
      scheduleWrite(listingId, listing, nextWishlisted);

      return Promise.resolve(nextWishlisted);
    },
    [listingIds, isAuthenticated, firebaseEnabled, applyOptimistic, redirectToLogin, scheduleWrite]
  );

  const isWishlisted = useCallback(
    listingId => (listingId ? listingIds.has(listingId) : false),
    [listingIds]
  );

  const isSyncing = useCallback(
    listingId => (listingId ? syncingIds.has(listingId) : false),
    [syncingIds]
  );

  const loadMore = useCallback(async () => {
    if (!userId || !lastDoc || !hasMore) {
      return { items: [], hasMore: false };
    }
    const page = await getUserWishlist(userId, { startAfterDoc: lastDoc });
    setItems(prev => [...prev, ...page.items]);
    setLastDoc(page.lastDoc);
    setHasMore(page.hasMore);
    const mergedIds = [...listingIds, ...page.items.map(i => i.listingId)];
    setListingIds(idsToSet(mergedIds));
    return page;
  }, [userId, lastDoc, hasMore, listingIds]);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo(
    () => ({
      items,
      listingIds,
      loading,
      error,
      firebaseReady,
      firebaseEnabled,
      hasMore,
      isWishlisted,
      isSyncing,
      toggleWishlist,
      loadMore,
      redirectToLogin,
      clearError,
      userId,
      isAuthenticated,
    }),
    [
      items,
      listingIds,
      loading,
      error,
      firebaseReady,
      firebaseEnabled,
      hasMore,
      isWishlisted,
      isSyncing,
      toggleWishlist,
      loadMore,
      redirectToLogin,
      clearError,
      userId,
      isAuthenticated,
    ]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return ctx;
};

export default WishlistContext;
